import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";

const Shape = ({ id }) => {
  switch (id) {
    case "pir": return <mesh><sphereGeometry args={[0.09, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2]} /><meshStandardMaterial color="#F8FAFC" roughness={0.3} /></mesh>;
    case "smoke": return <mesh><cylinderGeometry args={[0.14, 0.14, 0.05, 24]} /><meshStandardMaterial color="#F1F5F9" /></mesh>;
    case "siren": return <mesh><cylinderGeometry args={[0.08, 0.1, 0.16, 20]} /><meshStandardMaterial color="#DC2626" emissive="#7F1D1D" /></mesh>;
    case "ir_ac": return <mesh><sphereGeometry args={[0.07, 16, 12]} /><meshStandardMaterial color="#111827" roughness={0.2} /></mesh>;
    case "gas_valve": return <group><mesh><boxGeometry args={[0.16, 0.12, 0.12]} /><meshStandardMaterial color="#FBBF24" /></mesh><mesh position={[0, 0.1, 0]}><boxGeometry args={[0.2, 0.03, 0.03]} /><meshStandardMaterial color="#DC2626" /></mesh></group>;
    case "relay_light": return <mesh><boxGeometry args={[0.18, 0.12, 0.08]} /><meshStandardMaterial color="#2563EB" /></mesh>;
    case "card": return <mesh><boxGeometry args={[0.06, 0.14, 0.12]} /><meshStandardMaterial color="#F59E0B" /></mesh>;
    case "lpg": return <mesh><boxGeometry args={[0.14, 0.14, 0.06]} /><meshStandardMaterial color="#16A34A" /></mesh>;
    case "door": return <mesh><boxGeometry args={[0.05, 0.16, 0.04]} /><meshStandardMaterial color="#FFFFFF" /></mesh>;
    case "conveyor": return <mesh><boxGeometry args={[0.16, 0.2, 0.1]} /><meshStandardMaterial color="#475569" /></mesh>;
    case "ir_count": return <group><mesh><boxGeometry args={[0.1, 0.08, 0.08]} /><meshStandardMaterial color="#111827" /></mesh><mesh position={[0, 0, 0.045]}><sphereGeometry args={[0.022, 10, 10]} /><meshStandardMaterial color="#EF4444" emissive="#EF4444" emissiveIntensity={0.8} /></mesh></group>;
    case "estop": return <group><mesh><boxGeometry args={[0.14, 0.14, 0.08]} /><meshStandardMaterial color="#FACC15" /></mesh><mesh position={[0, 0, 0.07]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.055, 0.045, 0.06, 20]} /><meshStandardMaterial color="#DC2626" /></mesh></group>;
    case "curtain": return <group>{[-0.42, 0.42].map((x) => <mesh key={x} position={[x, 0, 0]}><boxGeometry args={[0.05, 0.7, 0.05]} /><meshStandardMaterial color="#FACC15" /></mesh>)}</group>;
    case "vib": return <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.05, 0.05, 0.03, 18]} /><meshStandardMaterial color="#E2E8F0" metalness={0.6} /></mesh>;
    case "motor_temp": return <mesh><cylinderGeometry args={[0.022, 0.022, 0.14, 10]} /><meshStandardMaterial color="#94A3B8" metalness={0.7} /></mesh>;
    case "stack_light": return <group>{[["#EF4444", 0.13], ["#F59E0B", 0], ["#22C55E", -0.13]].map(([c, y]) => <mesh key={c} position={[0, y, 0]}><cylinderGeometry args={[0.06, 0.06, 0.12, 18]} /><meshStandardMaterial color={c} transparent opacity={0.75} /></mesh>)}</group>;
    case "buzzer": return <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.06, 0.06, 0.06, 18]} /><meshStandardMaterial color="#1F2937" /></mesh>;
    default: return <mesh><boxGeometry args={[0.13, 0.17, 0.05]} /><meshStandardMaterial color="#E0F2FE" /></mesh>;
  }
};

const Led = ({ broken }) => {
  const m = useRef();
  useFrame(({ clock }) => { if (m.current) m.current.emissiveIntensity = broken ? 1.5 + Math.sin(clock.elapsedTime * 8) : 1.2; });
  return <mesh position={[0, 0.11, 0.03]}><sphereGeometry args={[0.025, 10, 10]} /><meshStandardMaterial ref={m} color={broken ? "#EF4444" : "#22C55E"} emissive={broken ? "#EF4444" : "#22C55E"} /></mesh>;
};

export const placedPositions = (lab, placements) => {
  const per = {};
  return Object.entries(placements).filter(([d, s]) => lab.devices[d] && lab.spots[s]).map(([d, s]) => {
    per[s] = (per[s] || 0) + 1;
    const [x, y, z] = lab.spots[s].p;
    const k = per[s] - 1;
    return [d, [x + (Math.abs(x) > 2.8 ? 0 : k * 0.24), y - (k ? 0.12 : 0), z + (Math.abs(x) > 2.8 ? k * 0.24 : 0)]];
  });
};

export const LabDevices = ({ lab, placements, selected, broken, labels, lang }) => (
  <group>
    {placedPositions(lab, placements).map(([d, p]) => (
      <group key={d} position={p}>
        <Shape id={d} />
        <Led broken={broken === d} />
        {(labels || selected === d || broken === d) && (
          <Html center distanceFactor={9} position={[0, 0.28, 0]} zIndexRange={[20, 0]}>
            <div className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold shadow ${broken === d ? "bg-red-600 text-white" : selected === d ? "bg-[#0F766E] text-white" : "bg-white/90 text-[#0B1F3A]"}`}>
              {lang === "hi" ? lab.devices[d].hi : lab.devices[d].en}
            </div>
          </Html>
        )}
      </group>
    ))}
  </group>
);

export const Spots = ({ lab, onPick, active }) => (
  <group>
    {Object.entries(lab.spots).map(([k, s]) => (
      <mesh key={k} position={s.p} onClick={(e) => { e.stopPropagation(); onPick(k); }}
        onPointerOver={() => { document.body.style.cursor = "pointer"; }} onPointerOut={() => { document.body.style.cursor = ""; }}>
        <sphereGeometry args={[active ? 0.11 : 0.07, 20, 20]} />
        <meshStandardMaterial color="#0EA5E9" emissive="#38BDF8" emissiveIntensity={active ? 1 : 0.4} transparent opacity={active ? 0.85 : 0.45} />
      </mesh>
    ))}
  </group>
);
